import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, SearchIcon, ChevronRightIcon, LockIcon, ShieldCheckIcon, CheckIcon, HeartHandshakeIcon, ReceiptIcon, BuildingIcon, LandmarkIcon, FileTextIcon, UsersIcon, MapPinIcon, NavigationIcon, HandHeartIcon, DownloadIcon, Share2Icon } from 'lucide-react';
import { useTheme } from '../utils/theme';
interface ZakaatScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
type Step = 'services' | 'orgs' | 'review' | 'otp' | 'success';
type OrgTab = 'zakat' | 'sadqaat';
interface Org {
  id: string;
  name: string;
  account: string;
  initials: string;
  color: string;
}
const MORE_SERVICES = [{
  id: 'zakat',
  label: 'Zakat / Donations',
  desc: 'Pay Zakat, Fitra and Sadqaat securely',
  icon: HandHeartIcon,
  highlight: true
}];
const ZAKAT_ORGS: Org[] = [{
  id: 'edhi',
  name: 'Edhi Foundation',
  account: 'PK24BOPM0001234567',
  initials: 'EF',
  color: '#1E7A46'
}, {
  id: 'skmt',
  name: 'Shaukat Khanum Memorial',
  account: 'PK11BOPM0009988776',
  initials: 'SK',
  color: '#C0392B'
}, {
  id: 'saylani',
  name: 'Saylani Welfare Trust',
  account: 'PK56BOPM0004455667',
  initials: 'SW',
  color: '#2E86C1'
}, {
  id: 'alkhidmat',
  name: 'Al Khidmat Foundation',
  account: 'PK78BOPM0007788991',
  initials: 'AK',
  color: '#117864'
}, {
  id: 'akhuwat',
  name: 'Akhuwat Foundation',
  account: 'PK33BOPM0002211009',
  initials: 'AF',
  color: '#7D3C98'
}];
const SADQAAT_ORGS: Org[] = [{
  id: 'jdc',
  name: 'JDC Foundation Pakistan',
  account: 'PK90BOPM0006677889',
  initials: 'JD',
  color: '#D68910'
}, {
  id: 'chhipa',
  name: 'Chhipa Welfare Association',
  account: 'PK45BOPM0003344556',
  initials: 'CW',
  color: '#CB4335'
}, {
  id: 'indus',
  name: 'Indus Hospital & Health Network',
  account: 'PK67BOPM0005566778',
  initials: 'IH',
  color: '#1F618D'
}, {
  id: 'transparent',
  name: 'Transparent Hands',
  account: 'PK12BOPM0008899001',
  initials: 'TH',
  color: '#148F77'
}];
const CUSTOMER = {
  name: 'KASHISH SOHAIL SHEWANI',
  account: '1234-5678-9012',
  balance: 250000
};
const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (dir: number) => ({
    x: dir < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const formatPKR = (n: number) => n.toLocaleString('en-US', {
  minimumFractionDigits: 0
});
export function ZakaatScreen({
  onBack,
  onNavigateHome
}: ZakaatScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<Step>('services');
  const [direction, setDirection] = useState(1);
  const [tab, setTab] = useState<OrgTab>('zakat');
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [amount, setAmount] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(15);
  const navigateTo = (next: Step, dir = 1) => {
    setDirection(dir);
    setStep(next);
  };
  // ---- Theme palette ----
  const isLight = !isDark && !isPlatinum;
  const textPri = isLight ? 'text-[#1A1A1A]' : 'text-white';
  const textSec = isLight ? 'text-[#6B6B6B]' : isPlatinum ? 'text-[#A0A0A5]' : 'text-[#B8A89E]';
  const pageBg = isLight ? 'bg-[#FFF5F0]' : isPlatinum ? 'bg-[#1C1C1E]' : 'bg-[#1A0E08]';
  const cardSurface = isLight ? 'bg-white border border-[#F0E8E3]' : isPlatinum ? 'bg-[#2C2C2E] border border-[rgba(160,160,165,0.12)]' : 'bg-[#2D1810] border border-[rgba(243,112,36,0.12)]';
  const maroon = isLight ? '#5A1408' : '#F37024';
  const donationType = tab === 'zakat' ? 'Zakat / Fitra' : 'Sadqaat';
  // OTP resend timer
  useEffect(() => {
    if (step !== 'otp') return;
    setResendTimer(15);
    setOtp(['', '', '', '', '', '']);
  }, [step]);
  useEffect(() => {
    if (step !== 'otp' || resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [step, resendTimer]);
  // Amount validation
  const amountNum = Number(amount);
  const amountValid = amount !== '' && amountNum > 0 && amountNum <= CUSTOMER.balance;
  const amountError = amount !== '' && (amountNum <= 0 ? 'Enter an amount greater than zero' : amountNum > CUSTOMER.balance ? 'Amount exceeds available balance' : '');
  const otpComplete = otp.every(d => d !== '');
  const handleOtpChange = (index: number, value: string) => {
    // Paste support
    if (value.length > 1) {
      const digits = value.replace(/[^\d]/g, '').slice(0, 6).split('');
      const next = ['', '', '', '', '', ''];
      digits.forEach((d, i) => next[i] = d);
      setOtp(next);
      const lastIdx = Math.min(digits.length, 6) - 1;
      if (lastIdx >= 0) document.getElementById(`zakat-otp-${Math.min(lastIdx + 1, 5)}`)?.focus();
      return;
    }
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) {
      document.getElementById(`zakat-otp-${index + 1}`)?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`zakat-otp-${index - 1}`)?.focus();
    }
  };
  const txnId = 'TXN20260012345';
  const refNumber = 'DON000987654';
  const now = new Date();
  const txnDateTime = now.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  // Reusable header
  const Header = ({
    title,
    onBackClick
  }: {
    title: string;
    onBackClick: () => void;
  }) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-3378">
      <button onClick={onBackClick} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-3379">
        <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-3380" />
      </button>
      <h1 className={`text-[18px] font-semibold ${textPri}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3381">
        {title}
      </h1>
      <button onClick={onNavigateHome} aria-label="Home" className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${isLight ? 'border-[#5A1408] text-[#5A1408]' : 'border-[#F37024] text-[#F37024]'}`} data-id="element-3382">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-3383" />
      </button>
    </div>;
  // ===== Screen 1: More Services =====
  const renderServices = () => <motion.div key="services" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3384">
      <Header title="More Services" onBackClick={onBack ?? (() => {})} data-id="element-3385" />
      <div className="px-6 mt-2" data-id="element-3386">
        <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-3387">
          Additional banking services at your fingertips.
        </p>
        <div className="space-y-3" data-id="element-3388">
          {MORE_SERVICES.map((s, idx) => {
          const Icon = s.icon;
          const isZakat = s.id === 'zakat';
          return <motion.button key={s.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: idx * 0.04
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            if (isZakat) navigateTo('orgs');
          }} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-colors ${isZakat ? isLight ? 'bg-[#F37024]/8 border-2 border-[#F37024]' : 'bg-[#F37024]/12 border-2 border-[#F37024]' : cardSurface}`} data-id="element-3389">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{
              background: isZakat ? '#F37024' : isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
            }} data-id="element-3390">
                  <Icon className="w-5 h-5" strokeWidth={2} style={{
                color: isZakat ? '#FFFFFF' : '#F37024'
              }} data-id="element-3391" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-3392">
                  <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3393">
                    {s.label}
                  </p>
                  <p className={`text-[12px] truncate ${textSec}`} data-id="element-3394">{s.desc}</p>
                </div>
                <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3395" />
              </motion.button>;
        })}
        </div>
      </div>
    </motion.div>;
  // ===== Screen 2: Org List =====
  const orgs = tab === 'zakat' ? ZAKAT_ORGS : SADQAAT_ORGS;
  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const renderOrgs = () => <motion.div key="orgs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3396">
      <Header title="Zakat / Fitra & Sadqaat" onBackClick={() => navigateTo('services', -1)} data-id="element-3397" />

      {/* Tabs */}
      <div className="px-6 mt-2" data-id="element-3398">
        <div className={`relative flex rounded-2xl p-1 ${isLight ? 'bg-[#F5EBE6]' : 'bg-[#2D1810]'}`} data-id="element-3399">
          <motion.div layout className="absolute top-1 bottom-1 rounded-xl bg-[#F37024] shadow-md" initial={false} animate={{
          left: tab === 'zakat' ? 4 : '50%',
          right: tab === 'zakat' ? '50%' : 4
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} data-id="element-3400" />
          <button onClick={() => {
          setTab('zakat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${tab === 'zakat' ? 'text-white' : textSec}`} data-id="element-3401">
            Zakat / Fitra
          </button>
          <button onClick={() => {
          setTab('sadqaat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${tab === 'sadqaat' ? 'text-white' : textSec}`} data-id="element-3402">
            Sadqaat
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 mt-5" data-id="element-3403">
        <div className={`flex items-center gap-3 px-4 rounded-2xl h-[52px] ${cardSurface}`} data-id="element-3404">
          <SearchIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3405" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search charitable organization" className={`w-full bg-transparent outline-none text-[14px] ${textPri} placeholder:opacity-50`} data-id="element-3406" />
        </div>
      </div>

      {/* Org list */}
      <div className="px-6 mt-5 space-y-3" data-id="element-3407">
        {filteredOrgs.length === 0 && <p className={`text-center text-[13px] mt-8 ${textSec}`} data-id="element-3408">
            No organizations found.
          </p>}
        {filteredOrgs.map((org, idx) => <motion.button key={org.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        delay: idx * 0.04
      }} whileTap={{
        scale: 0.98
      }} onClick={() => {
        setSelectedOrg(org);
        setAmount('');
        navigateTo('review');
      }} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left ${cardSurface}`} data-id="element-3409">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white text-[14px] font-bold" style={{
          background: org.color
        }} data-id="element-3410">
              {org.initials}
            </div>
            <div className="min-w-0 flex-1" data-id="element-3411">
              <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3412">
                {org.name}
              </p>
              <p className={`text-[12px] truncate ${textSec}`} data-id="element-3413">{org.account}</p>
            </div>
            <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3414" />
          </motion.button>)}
      </div>
    </motion.div>;
  // ===== Screen 3: Review =====
  const serviceCharge = 0;
  const totalAmount = amountNum + serviceCharge;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3415">
      <Header title="Review" onBackClick={() => navigateTo('orgs', -1)} data-id="element-3416" />
      <div className="px-6 mt-2 flex-1 overflow-y-auto" data-id="element-3417">
        {/* Customer account */}
        <div className={`rounded-2xl p-5 mb-4 ${cardSurface}`} data-id="element-3418">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3419">
            From Account
          </p>
          <p className={`text-[15px] font-semibold ${textPri}`} data-id="element-3420">
            {CUSTOMER.name}
          </p>
          <p className={`text-[13px] mt-1 ${textSec}`} data-id="element-3421">{CUSTOMER.account}</p>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5" data-id="element-3422">
            <span className={`text-[12px] ${textSec}`} data-id="element-3423">Available Balance</span>
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3424">
              PKR {formatPKR(CUSTOMER.balance)}
            </span>
          </div>
        </div>

        {/* Beneficiary */}
        <div className={`rounded-2xl p-5 mb-4 ${cardSurface}`} data-id="element-3425">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3426">
            Beneficiary
          </p>
          <div className="space-y-3" data-id="element-3427">
            <div className="flex justify-between gap-3" data-id="element-3428">
              <span className={`text-[13px] ${textSec}`} data-id="element-3429">Organization</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3430">
                {selectedOrg?.name}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3431">
              <span className={`text-[13px] ${textSec}`} data-id="element-3432">Account No.</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3433">
                {selectedOrg?.account}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3434">
              <span className={`text-[13px] ${textSec}`} data-id="element-3435">Donation Type</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3436">
                {donationType}
              </span>
            </div>
          </div>
        </div>

        {/* Amount input */}
        <div className="mb-4" data-id="element-3437">
          <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3438">
            Amount (PKR)
          </label>
          <input type="text" inputMode="numeric" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter amount" className={`w-full h-[54px] px-4 rounded-2xl text-[18px] font-semibold outline-none border-2 transition-colors ${textPri} ${amountError ? 'border-[#EF4444]' : 'border-transparent focus:border-[#F37024]'} ${cardSurface}`} data-id="element-3439" />
          {amountError && <p className="text-[12px] text-[#EF4444] mt-1.5 ml-1" data-id="element-3440">
              {amountError}
            </p>}
        </div>

        {/* Transaction summary */}
        <div className={`rounded-2xl p-5 mb-4 ${cardSurface}`} data-id="element-3441">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3442">
            Transaction Summary
          </p>
          <div className="space-y-3" data-id="element-3443">
            <div className="flex justify-between" data-id="element-3444">
              <span className={`text-[13px] ${textSec}`} data-id="element-3445">Donation Amount</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3446">
                PKR {amountNum > 0 ? formatPKR(amountNum) : '0'}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-3447">
              <span className={`text-[13px] ${textSec}`} data-id="element-3448">Service Charges</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3449">
                PKR {formatPKR(serviceCharge)}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-black/5" data-id="element-3450">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3451">
                Total Amount
              </span>
              <span className={`text-[14px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3452">
                PKR {amountNum > 0 ? formatPKR(totalAmount) : '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Security indicator */}
        <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl ${isLight ? 'bg-[#10B981]/8' : 'bg-[#10B981]/12'}`} data-id="element-3453">
          <ShieldCheckIcon className="w-5 h-5 text-[#10B981] shrink-0" strokeWidth={2} data-id="element-3454" />
          <span className={`text-[12px] font-medium ${textPri}`} data-id="element-3455">
            Secure Banking Transaction
          </span>
          <LockIcon className="w-4 h-4 text-[#10B981] ml-auto shrink-0" strokeWidth={2} data-id="element-3456" />
        </div>
      </div>

      <div className="px-6 mt-auto pt-6" data-id="element-3457">
        <button onClick={() => navigateTo('otp')} disabled={!amountValid} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3458">
          Pay Now
        </button>
      </div>
    </motion.div>;
  // ===== Screen 4: OTP =====
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3459">
      <Header title="Verification" onBackClick={() => navigateTo('review', -1)} data-id="element-3460" />
      <div className="px-4 sm:px-6 mt-4 flex-1 max-w-md mx-auto w-full" data-id="element-3461">
        <h2 className={`text-[20px] font-light mb-2 ${textPri}`} data-id="element-3462">Enter OTP</h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-3463">
          A 6-digit OTP has been sent to your registered mobile number.
        </p>

        <div className="grid grid-cols-6 gap-2 sm:gap-3 max-w-[340px] mx-auto mb-8" data-id="element-3464">
          {otp.map((digit, index) => <input key={index} id={`zakat-otp-${index}`} type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={index === 0 ? 6 : 1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className={`
                h-12 sm:h-14
                w-full
                rounded-xl
                text-center
                text-lg sm:text-xl
                font-bold
                outline-none
                border-2
                transition-colors
                ${digit ? 'border-[#F37024] bg-[#F37024]/10' : cardSurface}
                ${textPri}
              `} data-id="element-3465" />)}
        </div>

        <div className="flex items-center justify-center gap-1.5" data-id="element-3466">
          <span className={`text-[13px] ${textSec}`} data-id="element-3467">
            Didn't receive the OTP?
          </span>
          {resendTimer > 0 ? <span className="text-[13px] font-semibold" style={{
          color: maroon,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-3468">
              Resend in 00:{String(resendTimer).padStart(2, '0')}
            </span> : <button onClick={() => setResendTimer(15)} className="text-[13px] font-bold text-[#F37024] hover:underline" data-id="element-3469">
              Resend
            </button>}
        </div>
      </div>

      <div className="px-6 mt-auto pt-6" data-id="element-3470">
        <button onClick={() => navigateTo('success')} disabled={!otpComplete} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3471">
          Submit
        </button>
      </div>
    </motion.div>;
  const handleDownloadReceipt = () => {
    window.print();
  };
  const handleShareReceipt = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Donation Receipt',
          text: `Donation of PKR ${amountNum} successfully sent to ${selectedOrg?.name}`
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  // ===== Screen 5: Success =====
  const renderSuccess = () => <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3472">
      <Header title="Payment Complete" onBackClick={onNavigateHome ?? (() => {})} data-id="element-3473" />
      <div className="px-6 mt-2 flex-1 flex flex-col items-center" data-id="element-3474">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 14,
        stiffness: 200
      }} className="w-20 h-20 rounded-full bg-[#10B981]/15 flex items-center justify-center mt-6 mb-5" data-id="element-3475">
          <div className="w-14 h-14 rounded-full bg-[#10B981] flex items-center justify-center" data-id="element-3476">
            <CheckIcon className="w-8 h-8 text-white" strokeWidth={3} data-id="element-3477" />
          </div>
        </motion.div>
        <h2 className={`text-[22px] font-semibold mb-1 ${textPri}`} data-id="element-3478">
          Payment Successful
        </h2>
        <p className={`text-[14px] mb-7 ${textSec}`} data-id="element-3479">
          Your donation has been processed.
        </p>

        <div className={`w-full rounded-2xl p-5 ${cardSurface}`} data-id="element-3480">
          <div className="text-center pb-4 mb-4 border-b border-dashed border-black/10" data-id="element-3481">
            <p className={`text-[12px] ${textSec}`} data-id="element-3482">Paid Amount</p>
            <p className={`text-[28px] font-bold mt-1 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3483">
              PKR {amountNum > 0 ? formatPKR(amountNum) : '5,000'}
            </p>
          </div>
          <div className="space-y-3" data-id="element-3484">
            <div className="flex justify-between gap-3" data-id="element-3485">
              <span className={`text-[13px] ${textSec}`} data-id="element-3486">Organization</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3487">
                {selectedOrg?.name}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3488">
              <span className={`text-[13px] ${textSec}`} data-id="element-3489">Org. Account</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3490">
                {selectedOrg?.account}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3491">
              <span className={`text-[13px] ${textSec}`} data-id="element-3492">Transaction ID</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3493">
                {txnId}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3494">
              <span className={`text-[13px] ${textSec}`} data-id="element-3495">Date & Time</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3496">
                {txnDateTime}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3497">
              <span className={`text-[13px] ${textSec}`} data-id="element-3498">Reference No.</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3499">
                {refNumber}
              </span>
            </div>
          </div>
        </div>

        {/* Share & Download receipt */}
        <div className="w-full grid grid-cols-2 gap-3 mt-5" data-id="element-3500">
          <button onClick={handleDownloadReceipt} className={`py-3.5 rounded-2xl border-2 border-[#F37024] text-[#F37024] font-semibold text-[14px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${isLight ? 'bg-transparent' : 'bg-transparent'}`} data-id="element-3501">
            <DownloadIcon className="w-5 h-5" strokeWidth={2} data-id="element-3502" />
            Download
          </button>
          <button onClick={handleShareReceipt} className="py-3.5 rounded-2xl bg-[#F37024] text-white font-semibold text-[14px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-id="element-3503">
            <Share2Icon className="w-5 h-5" strokeWidth={2} data-id="element-3504" />
            Share
          </button>
        </div>
      </div>

      <div className="px-6 mt-auto pt-6" data-id="element-3505">
        <button onClick={onNavigateHome} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-3506">
          Done
        </button>
      </div>
    </motion.div>;
  return <div className={`w-full min-h-screen overflow-hidden ${pageBg}`} data-id="element-3507">
      <AnimatePresence mode="wait" custom={direction} data-id="element-3508">
        {step === 'services' && renderServices()}
        {step === 'orgs' && renderOrgs()}
        {step === 'review' && renderReview()}
        {step === 'otp' && renderOtp()}
        {step === 'success' && renderSuccess()}
      </AnimatePresence>
    </div>;
}